import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Container, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import { useGetPublicRestaurant } from "src/data/clientBooking/queries/clientBooking";
import { useGetPublicRestaurantMenu } from "src/data/publicMenu/queries/publicMenu";
import {
  colorPublicMenuBg,
  colorPublicMenuText,
  colorTextError,
} from "src/design/tokens";
import { CoverHero } from "./components/CoverHero";
import { MenuCategoryNav } from "./components/MenuCategoryNav";
import { RestaurantTagsRow } from "./components/RestaurantTagsRow";
import { MenuSection } from "./components/MenuSection";

const sectionDomId = (index: number) => `menu-section-${index}`;

const PublicMenuPage = () => {
  const { t } = useTranslation();
  const { restaurant_name = "", restaurant_city = "" } = useParams<{
    restaurant_name: string;
    restaurant_city: string;
  }>();

  const displayName = restaurant_name.replaceAll("-", " ");
  const displayCity = restaurant_city.replaceAll("-", " ");

  const {
    data: restaurant,
    isLoading: restaurantLoading,
    isError: restaurantError,
    error: restaurantErrorObj,
  } = useGetPublicRestaurant(displayName, displayCity);

  const {
    data: sections = [],
    isLoading: menuLoading,
    isError: menuError,
  } = useGetPublicRestaurantMenu(displayName, displayCity);

  const categories = useMemo(
    () => sections.map((s, i) => ({ id: sectionDomId(i), label: s.section })),
    [sections],
  );

  if (restaurantLoading || menuLoading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          backgroundColor: colorPublicMenuBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: colorPublicMenuText }} />
      </Box>
    );
  }

  const isNotFound =
    restaurantErrorObj instanceof Error &&
    restaurantErrorObj.message === "NOT_FOUND";

  if (restaurantError || menuError || !restaurant) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          backgroundColor: colorPublicMenuBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          px: 2,
          textAlign: "center",
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 40, color: colorTextError }} />
        <Typography sx={{ color: colorPublicMenuText, fontWeight: 500 }}>
          {t(isNotFound ? "publicMenu.restaurantNotFound" : "publicMenu.errorLoading")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: colorPublicMenuBg }}>
      <CoverHero restaurant={restaurant} />

      {categories.length > 0 && <MenuCategoryNav categories={categories} />}

      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}>
        <RestaurantTagsRow tags={restaurant.tags} />

        {sections.map((section, index) => (
          <MenuSection
            key={section.section}
            id={sectionDomId(index)}
            title={section.section}
            items={section.items}
          />
        ))}
      </Container>
    </Box>
  );
};

export default PublicMenuPage;
